import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import type { UserRef } from '@cyphex/types';
import { Avatar, Button, Input, Modal } from '@cyphex/ui';
import { usePeopleSearch } from '../../hooks/useCallsQueries';

export interface CallPeoplePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Label of the confirming button once somebody is chosen. */
  confirmLabel: string;
  /** Already on the call — offering them again would 409. */
  excludeUserIds: readonly string[];
  busy: boolean;
  onConfirm: (person: UserRef) => void;
  /** An extra choice rendered beside the primary one (e.g. "Consult first"). */
  secondary?: { label: string; onSelect: (person: UserRef) => void };
}

/**
 * Pick ONE person for an in-call action.
 *
 * Deliberately single-select: transfer has exactly one target, and "add people"
 * to a live call rings each addition on its own settings anyway — so choosing
 * one at a time is what the server actually does with the list.
 */
export function CallPeoplePicker(props: CallPeoplePickerProps) {
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<UserRef | null>(null);
  const people = usePeopleSearch(query);

  // A reopened picker must not remember the last person, or a second transfer
  // can be confirmed without ever choosing anybody.
  useEffect(() => {
    if (!props.open) {
      setQuery('');
      setChosen(null);
    }
  }, [props.open]);

  const results = (people.data ?? []).filter(
    (person) => !props.excludeUserIds.includes(person.id),
  );

  return (
    <Modal
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={props.title}
      description={props.description}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          {props.secondary === undefined || chosen === null ? null : (
            <Button
              variant="secondary"
              disabled={props.busy}
              onClick={() => props.secondary?.onSelect(chosen)}
            >
              {props.secondary.label}
            </Button>
          )}
          <Button
            variant="primary"
            disabled={chosen === null || props.busy}
            onClick={() => (chosen === null ? undefined : props.onConfirm(chosen))}
          >
            {props.confirmLabel}
          </Button>
        </>
      }
    >
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search people"
        label="Search people"
        hideLabel
        variant="search"
        leadingIcon={Search}
      />

      {results.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          {query.trim().length > 1 ? 'Nobody matches that.' : 'Type a name to search.'}
        </p>
      ) : (
        <ul className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-border">
          {results.map((person) => {
            const selected = chosen?.id === person.id;
            return (
              <li key={person.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setChosen(person)}
                  className={`flex w-full items-center gap-3 border-b border-border px-3 py-2 text-left last:border-b-0 ${
                    selected ? 'bg-primary-subtle' : 'hover:bg-subtle'
                  }`}
                >
                  <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size="md" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-medium text-foreground">
                      {person.displayName}
                    </span>
                    {person.jobTitle === null ? null : (
                      <span className="block truncate text-sm text-muted-foreground">
                        {person.jobTitle}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
